import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Search } from "lucide-react";
import { api } from "@/lib/apiClient";
import { guestTypeLabel } from "@/lib/guestType";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { bookingStatusLabel } from "@/lib/bookingStatus";
import { formatLicensePlate, vehicleTypeLabel } from "@/lib/vehicle";
import { COMMAND_PALETTE_NAV, type NavItem } from "@/lib/nav";
import { NavIcon } from "@/navigation/NavIcon";
import { useNav } from "@/navigation/navContext";
import { useNavAccess } from "@/navigation/useNavAccess";
import { usePageHeader } from "@/layout/usePageHeader";
import { buildBookingsOpenUrl } from "@/lib/bookingNavigation";
import type { SearchResponse } from "@/types/camping";

const DEBOUNCE_MS = 220;

type PaletteRow =
  | { kind: "nav"; item: NavItem }
  | { kind: "guest"; id: string; title: string; subtitle: string }
  | { kind: "booking"; id: string; status: string; title: string; subtitle: string };

export function CommandPalette() {
  const { commandPaletteOpen } = useNav();
  if (!commandPaletteOpen) return null;
  return <CommandPaletteDialog />;
}

function CommandPaletteDialog() {
  const listboxId = useId();
  const navigate = useNavigate();
  const { commandPaletteOpen, setCommandPaletteOpen } = useNav();
  const { meta } = usePageHeader();
  const allowedNav = useNavAccess(COMMAND_PALETTE_NAV);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const q = query.trim().toLowerCase();
  const isSearchMode = q.length >= 2;

  const navRows: PaletteRow[] = useMemo(() => {
    if (isSearchMode) return [];
    const filtered = q
      ? allowedNav.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            item.href.toLowerCase().includes(q) ||
            item.description?.toLowerCase().includes(q) ||
            item.keywords?.some((keyword) => keyword.toLowerCase().includes(q)),
        )
      : allowedNav;
    return filtered.slice(0, 12).map((item) => ({ kind: "nav" as const, item }));
  }, [allowedNav, isSearchMode, q]);

  const searchRows: PaletteRow[] = useMemo(() => {
    if (!isSearchMode || !results) return [];
    const rows: PaletteRow[] = [];
    for (const g of results.guests) {
      rows.push({
        kind: "guest",
        id: g.id,
        title: `${g.last_name}, ${g.first_name}`,
        subtitle: guestTypeLabel(g.guest_type),
      });
    }
    for (const b of results.bookings) {
      rows.push({
        kind: "booking",
        id: b.id,
        status: b.status,
        title: `${b.booking_number} · ${b.guest_name}`,
        subtitle: [
          formatIsoDateShort(b.arrival_date),
          formatIsoDateShort(b.departure_date),
          b.pitch_code,
          b.license_plate ? formatLicensePlate(b.license_plate, null) : null,
          b.vehicle_type ? vehicleTypeLabel(b.vehicle_type) : null,
          bookingStatusLabel(b.status),
        ]
          .filter(Boolean)
          .join(" · "),
      });
    }
    return rows.slice(0, 14);
  }, [isSearchMode, results]);

  const rows = isSearchMode ? searchRows : navRows;

  const runSearch = useCallback(async (term: string) => {
    const trimmed = term.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<SearchResponse>(
        `/api/v1/search?q=${encodeURIComponent(trimmed)}&limit=10`,
      );
      setResults(data);
    } catch (err) {
      setResults(null);
      setError(err instanceof Error ? err.message : "Suche fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!commandPaletteOpen) return;
    const t = window.setTimeout(() => void runSearch(query), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [commandPaletteOpen, query, runSearch]);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery("");
      setResults(null);
      setHighlightIndex(0);
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query, rows.length]);

  function close() {
    setCommandPaletteOpen(false);
    setQuery("");
  }

  function goToGuest(id: string) {
    close();
    if (meta?.onSelectGuest) {
      meta.onSelectGuest(id);
      return;
    }
    navigate(`/guests/${encodeURIComponent(id)}`);
  }

  function goToBooking(id: string, bookingStatus?: string) {
    close();
    if (meta?.onSelectBooking) {
      meta.onSelectBooking(id);
      return;
    }
    navigate(buildBookingsOpenUrl(id, bookingStatus));
  }

  function activateRow(row: PaletteRow) {
    if (row.kind === "nav") {
      close();
      navigate(row.item.href);
      return;
    }
    if (row.kind === "guest") goToGuest(row.id);
    if (row.kind === "booking") goToBooking(row.id, row.status);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(0, rows.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && rows[highlightIndex]) {
      e.preventDefault();
      activateRow(rows[highlightIndex]);
    }
  }

  return (
    <Transition show as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={close}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/50" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 flex items-start justify-center px-4 pt-[max(12vh,var(--eh-safe-top))] sm:pt-[18vh]">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-[0.98]"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-[0.98]"
          >
            <DialogPanel className="w-full max-w-xl overflow-hidden rounded-xl border border-separator bg-card shadow-ios-lg">
              <div className="flex items-center gap-2 border-b border-separator px-3 py-2.5">
                <Search size={18} className="shrink-0 text-label-tertiary" aria-hidden />
                <input
                  ref={inputRef}
                  id={`${listboxId}-input`}
                  type="search"
                  className="min-w-0 flex-1 bg-transparent text-sm text-label outline-none placeholder:text-label-tertiary"
                  placeholder="Bereich, Gast, Buchung, Kennzeichen…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={rows.length > 0}
                  aria-controls={`${listboxId}-list`}
                />
                <kbd className="hidden rounded border border-separator px-1.5 py-0.5 text-[0.625rem] text-label-tertiary sm:inline">
                  Esc
                </kbd>
              </div>

              <div
                id={`${listboxId}-list`}
                role="listbox"
                className="max-h-[min(24rem,55vh)] overflow-y-auto py-1"
              >
                {loading ? (
                  <p className="px-4 py-3 text-sm text-label-secondary">Suche…</p>
                ) : error ? (
                  <p className="px-4 py-3 text-sm text-status-red">{error}</p>
                ) : rows.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-label-secondary">
                    {isSearchMode ? "Keine Treffer" : "Tippen Sie zum Filtern oder suchen Sie ab 2 Zeichen"}
                  </p>
                ) : (
                  <>
                    {!isSearchMode ? (
                      <p className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-label-tertiary">
                        Bereiche
                      </p>
                    ) : null}
                    <ul>
                      {rows.map((row, index) => {
                        const highlighted = index === highlightIndex;
                        if (row.kind === "nav") {
                          return (
                            <li key={`nav-${row.item.key}`}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={highlighted}
                                className={clsx(
                                  "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors",
                                  highlighted ? "bg-fill" : "hover:bg-fill",
                                )}
                                onClick={() => activateRow(row)}
                                onMouseEnter={() => setHighlightIndex(index)}
                              >
                                {row.item.icon ? (
                                  <NavIcon name={row.item.icon} size={18} />
                                ) : null}
                                <span className="min-w-0 flex-1">
                                  <span className="font-medium text-label">{row.item.label}</span>
                                  <span className="mt-0.5 block truncate text-xs text-label-secondary">
                                    {row.item.href}
                                  </span>
                                </span>
                              </button>
                            </li>
                          );
                        }
                        const label = row.kind === "guest" ? "Gast" : "Buchung";
                        return (
                          <li key={`${row.kind}-${row.id}`}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={highlighted}
                              className={clsx(
                                "flex w-full flex-col px-4 py-2.5 text-left text-sm transition-colors",
                                highlighted ? "bg-fill" : "hover:bg-fill",
                              )}
                              onClick={() => activateRow(row)}
                              onMouseEnter={() => setHighlightIndex(index)}
                            >
                              <span className="text-xs font-medium uppercase text-label-tertiary">
                                {label}
                              </span>
                              <span className="font-medium text-label">{row.title}</span>
                              <span className="text-xs text-label-secondary">{row.subtitle}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
